import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Stack, IconButton, Button, Avatar, CircularProgress } from '@mui/material';
import ReactPlayer from 'react-player';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ShareIcon from '@mui/icons-material/Share';
import { getPublicUrlFromFileKey } from '../../services/API/Api';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import COLORS from '../../Utils/colors';
import { BookmarkBorderRounded, Bookmark } from '@mui/icons-material';
import BottomModal from '../../Components/BottomModel';
import Comments from '../Post/Comments';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';

interface ShortsCardProps {
  item: any;
  index: number;
  activeIndex: number;
  handleFollow: () => void;
  handleShare: () => void;
  videoRefs: React.RefObject<HTMLDivElement[]>;
  isPreview: boolean;
  currentUser: any;
  onLike?: (id: string, isLiked: boolean) => void;
  onBookmark?: (id: string, isSaved: boolean) => void;
}

export const ShortsCard: React.FC<ShortsCardProps> = ({
  item,
  index,
  activeIndex,
  handleFollow,
  handleShare,
  videoRefs,
  isPreview,
  currentUser,
  onLike,
  onBookmark
}) => {
  // Destructure all needed fields from item
  const {
    author = {},
    description = '',
    files = [],
    likes = [],
    location = {},
    place = {},
    isLiked = false,
    likeCount = 0,
    isFollowing = false,
    _id = '',
  } = item || {};
  const user = author.username || author.name || '';
  // Prefer direct fileURL; fallback logic will generate a signed URL from fileKey if necessary
  const fileURL = files[0]?.fileURL || '';
  const fileKey = files[0]?.fileKey || '';
  const [videoUrl, setVideoUrl] = useState<string>(fileURL || '');
  // Track whether we've already attempted to fetch a signed URL to avoid repeated calls
  const hasTriedSignedRef = useRef(false);
  const playerRef = useRef<ReactPlayer>(null);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for Safari autoplay compatibility
  
  // Track video visibility and playing state
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Comment modal state
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  
  // Local state for optimistic updates
  const [localIsLiked, setLocalIsLiked] = useState(isLiked);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount || 0);
  const [localIsSaved, setLocalIsSaved] = useState(item?.isSaved || false);
  
  // Navigation and authentication
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: RootState) => state.auth.isLoggedIn);

  // Constants for text truncation
  const MAX_DESCRIPTION_LENGTH = 50;
  const shouldShowSeeMore = description.length > MAX_DESCRIPTION_LENGTH;

  const handleToggleMute = () => {
    // Only allow mute toggle when this video is visible
    if (isVisible) {
      setIsMuted((prev) => !prev);
      setShowControls(true);
    }
  };

  const toggleDescription = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const handleCommentClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setCommentModalOpen(true);
  };

  const handleLikeClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // Optimistic update
    const newIsLiked = !localIsLiked;
    const newLikeCount = newIsLiked ? localLikeCount + 1 : localLikeCount - 1;
    
    setLocalIsLiked(newIsLiked);
    setLocalLikeCount(newLikeCount);
    
    // Call parent handler
    if (onLike) {
      onLike(_id, newIsLiked);
    }
  };

  const handleBookmarkClick = () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    
    // Optimistic update
    setLocalIsSaved(!localIsSaved);
    
    // Call parent handler
    if (onBookmark) {
      onBookmark(_id, !localIsSaved);
    }
  };

  // Auto-hide controls after 2 seconds
  useEffect(() => {
    if (showControls) {
      const timeout = setTimeout(() => {
        setShowControls(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [showControls]);

  // Check video visibility and control playback using IntersectionObserver
  useEffect(() => {
    const videoElement = videoRefs.current[index];
    
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isIntersecting = entry.isIntersecting;
          const intersectionRatio = entry.intersectionRatio;
          
          // Video is visible if it's intersecting and at least 60% visible
          const shouldBeVisible = isIntersecting && intersectionRatio >= 0.6;
          
          if (shouldBeVisible !== isVisible) {
            console.log(`Video ${index}: visibility changed to ${shouldBeVisible} (ratio: ${intersectionRatio.toFixed(2)})`);
            setIsVisible(shouldBeVisible);
            
            if (shouldBeVisible) {
              console.log(`Starting video ${index}`);
              setIsPlaying(true);
            } else {
              console.log(`Stopping video ${index}`);
              setIsPlaying(false);
            }
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
        rootMargin: '0px'
      }
    );

    observer.observe(videoElement);
    
    return () => {
      observer.disconnect();
    };
  }, [index, isVisible]);


  // Initialize video source. If a direct URL exists, use it; otherwise try to generate a signed URL.
  useEffect(() => {
    const initializeVideo = async () => {
      setIsLoading(true);
      if (fileURL) {
        // Direct URL available – try this first
        setVideoUrl(fileURL);
        setIsLoading(false);
      } else if (fileKey && !isPreview) {
        // No direct URL – generate signed URL immediately
        try {
          const response = await getPublicUrlFromFileKey(fileKey);
          setVideoUrl(response?.data?.files[0]?.viewUrl);
          setIsLoading(false);
        } catch (error) {
          console.log('error fetching public url', error);
        } finally {
          setIsLoading(false);
        }
      } else {
        // Neither URL nor key available – stop loading spinner
        setIsLoading(false);
      }
    };

    initializeVideo();
    // Reset signed-URL attempt flag whenever source data changes
    hasTriedSignedRef.current = false;
  }, [fileURL, fileKey]);
  useEffect(() => {
    // Cleanup on unmount: blur the video element to prevent keyboard control
    return () => {
      // Stop the video when unmounting
      if (playerRef.current) {
        playerRef.current.getInternalPlayer()?.pause();
      }
      
      // Try to find the underlying video element and blur it
      const videoEl = document.querySelector('video');
      if (videoEl && document.activeElement === videoEl) {
        (videoEl as HTMLVideoElement).blur();
      }
    };
  }, []);

  // Cleanup when playing state changes to prevent conflicts
  useEffect(() => {
    return () => {
      // Ensure video is paused when playing state changes
      if (playerRef.current) {
        playerRef.current.getInternalPlayer()?.pause();
      }
    };
  }, [isPlaying]);
  return (
    <>
      <BottomModal
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        title="Comments"
        children={<Comments postId={_id} />}
      />
      <Box
        onClick={(e) => {
          e.stopPropagation();
          if (isVisible) {
            handleToggleMute();
          }
        }}
      ref={(el) => {
        if (el) {
          videoRefs.current[index] = el as HTMLDivElement;
        }
      }}
      sx={{
        height: 'calc(100vh - 56px)',
        scrollSnapAlign: 'start',
        position: 'relative',
        backgroundColor: 'black',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* Test with both ReactPlayer and HTML5 video */}
      <ReactPlayer
        key={`${index}-${isPlaying}`}
        ref={playerRef}
        url={videoUrl}
        playing={isPlaying}
        loop
        muted={isMuted}
        controls={false}
        width="100%"
        height="100%"
        style={{ objectFit: 'cover', pointerEvents: 'none' } as React.CSSProperties}
        volume={1}
        onReady={() => {
          setIsLoading(false);
          console.log(`Video ${index} ready`);
        }}
        onStart={() => {
          setIsLoading(false);
          console.log(`Video ${index} started playing`);
        }}
        onBuffer={() => {
          setIsLoading(true);
        }}
        onBufferEnd={() => {
          setIsLoading(false);
        }}
        onPlay={() => {
          console.log(`Video ${index} is now playing`);
        }}
        onPause={() => {
          console.log(`Video ${index} is now paused`);
        }}
        onEnded={() => {
          console.log(`Video ${index} ended`);
        }}
        onError={(e) => {
          setIsLoading(true);
          // If the direct URL fails, attempt to generate and use a signed URL
          (async () => {
            if (!hasTriedSignedRef.current && fileKey && !isPreview) {
              hasTriedSignedRef.current = true;
              try {
                const response = await getPublicUrlFromFileKey(fileKey);
                setVideoUrl(response?.data?.files[0]?.viewUrl);
                setIsLoading(false);
              } catch (error) {
                console.log('error fetching public url on player error', error);
                console.log('shorts onError 2', videoUrl);  
              }
            }
          })();
        }}
      />



      {/* Loader */}
      {isLoading && (
        <CircularProgress
          size={60}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'orange',
            zIndex: 12,
          }}
        />
      )}

      {/* Custom Mute/Unmute button */}
      {showControls && isVisible && (
        <IconButton
          onClick={handleToggleMute}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'white',
            zIndex: 10,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.7)',
            },
          }}
        >
          {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      )}

      {/* User Info and Caption */}
      <Box
        sx={{
          position: 'absolute',
          // if it is the last video, make it sticky to the bottom xs:70 sm:40
          bottom: { xs: 20, sm: 20 },
          left: 30,
          color: 'white',
          zIndex: 1,
          maxWidth: '80%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: 40,
          }}
        >
          <Avatar 
            src={author.profilePicture || ''} 
            sx={{ 
              width: 32, 
              height: 32, 
              mr: 1, 
              cursor: 'pointer',
              zIndex: 16
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isLoggedIn) {
                navigate('/login');
                return;
              }
              navigate(`/profile/${author._id}`);
            }}
          />
          <Typography
            fontWeight="bold"
            fontSize="16px"
            color="white"
            sx={{ lineHeight: '32px', height: 32, display: 'flex', alignItems: 'center' }}
          >
            {user}
          </Typography>
          {!isPreview && author?.username !== currentUser?.username && (
            <Button
              variant="outlined"
              sx={{ 
                ml: 1.5, 
                height: 28, 
                minWidth: 60, 
                px: 1, 
                borderRadius: '6px', 
                color: 'white', 
                borderColor: 'white', 
                textTransform: 'none',
                zIndex: 15,
                position: 'relative'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleFollow();
              }}
            >
              {isFollowing ? "Following" : "Follow"}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              lineHeight: 1.4,
              wordBreak: 'break-word'
            }}
          >
            {isDescriptionExpanded 
              ? description 
              : description.length > MAX_DESCRIPTION_LENGTH 
                ? `${description.substring(0, MAX_DESCRIPTION_LENGTH)}...` 
                : description
            }
          </Typography>
          {shouldShowSeeMore && (
            <Button
              onClick={toggleDescription}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 600,
                p: 0,
                minWidth: 'auto',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: 'transparent',
                  textDecoration: 'underline',
                }
              }}
            >
              {isDescriptionExpanded ? 'See less' : 'See more'}
            </Button>
          )}
        </Box>

        {/* Location */}
        <Stack direction="row" sx={{ mt: 1 }}>
          <LocationOnIcon sx={{ fontSize: 'sm', color: '#4FC3F7' }} />
          <Typography sx={{ color: '#4FC3F7', fontSize: 'md', fontWeight: 'sm', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{location?.name}</Typography>
        </Stack>
        {/* Places */}
        <Stack 
          direction="row" 
          sx={{ 
            mt: 1, 
            mx: 0,
            cursor: 'pointer',
            zIndex: 16
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (!isLoggedIn) {
              navigate('/login');
              return;
            }
            navigate(`/places/${place?._id}`);
          }}
        >
          <MapOutlinedIcon sx={{ fontSize: 'sm', color: '#4FC3F7' }} />
          <Typography sx={{ color: '#4FC3F7', fontSize: 'md', fontWeight: 'sm' }}>{place?.name}</Typography>
        </Stack>
      </Box>

      {/* Interaction Buttons */}
      <Stack
        spacing={2}
        sx={{
          position: 'absolute',
          right: 2,
          bottom: { xs: 100, sm: 40 },
          alignItems: 'center',
          zIndex: 15,
        }}
      >
        {/* Like */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleLikeClick();
            }}
            sx={{ color: 'white', zIndex: 16 }}
          >
            {localIsLiked ? (
              <FavoriteIcon sx={{ fontSize: 30, color: 'red' }} />
            ) : (
              <FavoriteBorderIcon sx={{ fontSize: 30 }} />
            )}
          </IconButton>
          <Typography color="white">
            {localLikeCount ?? 0}
          </Typography>
        </Box>

        {/* Comment */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton onClick={(e) => {
            e.stopPropagation();
            handleCommentClick();
          }} sx={{ color: 'white', zIndex: 16 }} >
            <ChatBubbleOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
          {/* <Typography color="white" variant="caption">
              Comment
            </Typography> */}
        </Box>

        {/* Share */}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton onClick={handleShare} sx={{ color: 'white', zIndex: 16 }}>
            <ShareIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleBookmarkClick();
            }}
            sx={{ color: 'white', zIndex: 16 }}
          >
            {localIsSaved ? (
              <Bookmark sx={{ color: COLORS.primary, fontSize: 30 }} />
            ) : (
              <BookmarkBorderRounded sx={{ fontSize: 30 }} />
            )}
          </IconButton>
        </Box>
      </Stack>
    </Box>
    </>
  );
};